/**
 * @component ry-search-list
 * @category  nav
 * @summary   Always-visible, filterable, keyboard-navigable list (ry-ui-only). Distinct from `<ry-combobox>` (which is a single-select with a dropdown) — this is the inline directory/sidebar pattern: an input at the top, an always-visible scrollable list below. Supports optional grouping via `<ry-search-group label="...">`. Filter uses three-tier fuzzy matching: prefix > substring > char-in-order.
 *
 * @attribute placeholder  string?   default="Search..."   Input placeholder.
 * @attribute value        string?              Currently-selected item's value. Live property accessor.
 *
 * @property  value   string   Live value getter/setter. Setter finds + selects the matching item; unknown values are a silent no-op.
 *
 * @method    clear()   Clear the filter input + reset filter state (all items visible).
 *
 * @event ry:filter
 *        detail: { query: string, count: number }
 *        fires:  on every input keystroke. `query` is the trimmed input text; `count` is the number of items still visible after filtering.
 *
 * @event ry:highlight
 *        detail: { value: string, label: string }
 *        fires:  when the keyboard-navigation highlight moves to a new item (ArrowUp/Down). Hover does NOT fire this — it's keyboard-only signal so consumers can update a preview pane without mouse-noise.
 *
 * @event ry:beforeselect
 *        detail: { value: string, label: string, previous: string }
 *        fires:  on click, Enter on the highlighted item, or `el.value = v` — before `aria-selected`, the `value` attribute, or the input/filter change. `previous` is the current `value`.
 *        cancelable: preventDefault() keeps the current selection and the typed filter; `ry:select` does not fire
 *
 * @event ry:select
 *        detail: { value: string, label: string }
 *        fires:  on click OR Enter on the highlighted item. The selected item gets `aria-selected="true"`; the input is cleared + the filter resets.
 *
 * @keyboard  Input focused: ArrowDown / ArrowUp — move the highlight (wraps). Enter — select the highlighted item. Escape — clear the input. Other keys type into the input.
 *
 * @state     Per-item `aria-selected="true|false"`. Per-item `data-ry-highlighted` when keyboard nav lands on it. `[hidden]` on filtered-out items (and on groups with zero matching items).
 *
 * @content   `<ry-search-item value="..."> {label} </ry-search-item>` flat OR grouped inside `<ry-search-group label="...">`. The wrapper transforms these into the rendered list at setup.
 *
 * @dom
 *   ry-search-list[placeholder?][value?]
 *     > div.ry-search-list__search   [data-ry-target="search"]
 *       > input.ry-search-list__input  [data-ry-target="input"]   role="combobox"  aria-expanded="true"
 *     > div.ry-search-list__list   [data-ry-target="list"]   role="listbox"
 *       > div.ry-search-list__group   [data-ry-target="group"]   (only when authored with <ry-search-group>)
 *         > div.ry-search-list__group-label   {label}
 *         > div.ry-search-list__item   [data-ry-target="item"]   role="option"   data-value   aria-selected
 *           > span   [data-ry-target="item-label"]   {label}
 *       > div.ry-search-list__item   (flat items if no groups)
 *     > div.ry-search-list__empty   [data-ry-target="empty"]   hidden   {"No results"}
 *
 * @gotcha  Filter uses three-tier fuzzy matching: (1) prefix match (best), (2) substring match (good), (3) char-in-order match (fuzzy). When the input is empty, ALL items are visible. The match score affects highlight order — better-scoring items appear visually higher when the filter is active (via sort by ry-order being NOT applied during search, only when clearing).
 *
 * @gotcha  `ry:highlight` fires ONLY on keyboard nav, not on mouse hover. This is intentional — hovering items in a long list shouldn't spam consumers' preview-update handlers.
 *
 * @gotcha  Selecting an item clears the search input. If you want the input to retain the user's text post-selection, listen to `ry:select` and re-set the input — but it'll then re-filter on the next keystroke.
 *
 * @gotcha  Setting `el.value = "unknown-id"` is a silent no-op (no warning, previous value preserved). The setter calls `#selectItem` only when a matching child is found. Verify your value against the authored items before setting.
 *
 * @gotcha  Empty groups (all items hidden by filter) get `[hidden]` automatically — the group label disappears alongside its items. No JS exposure for this; it's a side effect of `#filterItems`.
 *
 * @gotcha  `<ry-search-item>` and `<ry-search-group>` are NOT live custom elements — they're authoring conventions parsed at setup. After mount, the original tags are REPLACED with the rendered `[data-ry-target="item"]` / `[data-ry-target="group"]` markup. Don't query for them post-mount.
 *
 * @example flat
 * ```html
 * <ry-search-list placeholder="Search components...">
 *   <ry-search-item value="grid">Grid</ry-search-item>
 *   <ry-search-item value="tabs">Tabs</ry-search-item>
 *   <ry-search-item value="modal">Modal</ry-search-item>
 * </ry-search-list>
 * ```
 *
 * @example grouped
 * ```html
 * <ry-search-list placeholder="Search...">
 *   <ry-search-group label="Layout">
 *     <ry-search-item value="grid">Grid</ry-search-item>
 *     <ry-search-item value="stack">Stack</ry-search-item>
 *   </ry-search-group>
 *   <ry-search-group label="Forms">
 *     <ry-search-item value="input">Input</ry-search-item>
 *     <ry-search-item value="select">Select</ry-search-item>
 *   </ry-search-group>
 * </ry-search-list>
 * ```
 *
 * @tutorial mount-renders-search-input-and-list
 * ```js
 * const el = mount(`
 *   <ry-search-list placeholder="Find...">
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * await expectEqual(!!el.querySelector('[data-ry-target="input"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="list"]'), true);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 2);
 * ```
 *
 * @tutorial typing-filters-by-fuzzy-match
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *     <ry-search-item value="c">Cherry</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'an';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * const visible = Array.from(el.querySelectorAll('[data-ry-target="item"]:not([hidden])'));
 * // Banana matches "an" (substring); fuzzy could also match "Apple" (a..n? no) or Cherry (no)
 * await expectEqual(visible.length >= 1, true, 'at least Banana matches');
 * const labels = visible.map((v) => v.textContent.trim());
 * await expectEqual(labels.includes('Banana'), true);
 * ```
 *
 * @tutorial filter-fires-ry-filter-with-count
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * const fired = [];
 * el.addEventListener('ry:filter', (e) => fired.push(e.detail));
 * input.value = 'q';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].query, 'q');
 * await expectEqual(typeof fired[0].count, 'number');
 * ```
 *
 * @tutorial empty-query-shows-all
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'app';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * input.value = '';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * const visible = el.querySelectorAll('[data-ry-target="item"]:not([hidden])');
 * await expectEqual(visible.length, 2, 'all items visible on empty query');
 * ```
 *
 * @tutorial click-item-fires-ry-select-and-clears-input
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="grid">Grid</ry-search-item>
 *     <ry-search-item value="tabs">Tabs</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'gri';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * const item = el.querySelector('[data-value="grid"]');
 * const fired = [];
 * el.addEventListener('ry:select', (e) => fired.push(e.detail));
 * item.click();
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].value, 'grid');
 * await expectEqual(fired[0].label, 'Grid');
 * await expectAttr(item, 'aria-selected', 'true');
 * await expectEqual(input.value, '', 'input cleared after select');
 * ```
 *
 * @tutorial beforeselect-veto
 * ```js
 * // preventDefault() on ry:beforeselect keeps the previous selection (and the
 * // typed filter) and ry:select stays silent; without the veto the click selects.
 * const el = mount(`
 *   <ry-search-list value="grid">
 *     <ry-search-item value="grid">Grid</ry-search-item>
 *     <ry-search-item value="tabs">Tabs</ry-search-item>
 *   </ry-search-list>
 * `);
 * const selected = [];
 * el.addEventListener('ry:select', (e) => selected.push(e.detail.value));
 * const befores = [];
 * el.addEventListener('ry:beforeselect', (e) => { befores.push(e.detail); e.preventDefault(); }, { once: true });
 * el.querySelector('[data-value="tabs"]').click();
 * await expectEqual(el.value, 'grid', 'vetoed select keeps the previous value');
 * await expectEqual(befores[0].previous, 'grid', 'detail.previous is the current value');
 * await expectEqual(selected.length, 0, 'ry:select did not fire');
 * el.querySelector('[data-value="tabs"]').click();
 * await expectEqual(el.value, 'tabs', 'without the veto, the click selects');
 * await expectEqual(selected[0], 'tabs');
 * ```
 *
 * @tutorial keyboard-arrow-moves-highlight-and-fires-ry-highlight
 * ```js
 * // Wrong assumption: hover fires ry:highlight. It does NOT — keyboard-only.
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * const fired = [];
 * el.addEventListener('ry:highlight', (e) => fired.push(e.detail.value));
 * input.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * await expectEqual(fired.length >= 1, true, 'keyboard nav fired ry:highlight');
 * ```
 *
 * @tutorial grouped-items-render-under-group-label
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-group label="Layout">
 *       <ry-search-item value="grid">Grid</ry-search-item>
 *     </ry-search-group>
 *     <ry-search-group label="Forms">
 *       <ry-search-item value="input">Input</ry-search-item>
 *     </ry-search-group>
 *   </ry-search-list>
 * `);
 * const groups = el.querySelectorAll('[data-ry-target="group"]');
 * await expectEqual(groups.length, 2);
 * const items = el.querySelectorAll('[data-ry-target="item"]');
 * await expectEqual(items.length, 2);
 * ```
 *
 * @tutorial unknown-value-setter-is-silent-noop
 * ```js
 * const el = mount(`
 *   <ry-search-list value="a">
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * el.value = 'zzz-not-real';
 * // setter no-ops; previous value attribute unchanged
 * await expectEqual(el.value, 'a');
 * ```
 *
 * @tutorial clear-method-resets-input-and-filter
 * ```js
 * const el = mount(`
 *   <ry-search-list>
 *     <ry-search-item value="a">Apple</ry-search-item>
 *     <ry-search-item value="b">Banana</ry-search-item>
 *   </ry-search-list>
 * `);
 * const input = el.querySelector('[data-ry-target="input"]');
 * input.value = 'app';
 * input.dispatchEvent(new Event('input', { bubbles: true }));
 * el.clear();
 * await expectEqual(input.value, '', 'input cleared');
 * const visible = el.querySelectorAll('[data-ry-target="item"]:not([hidden])');
 * await expectEqual(visible.length, 2, 'all items visible after clear');
 * ```
 *
 * @see ry-combobox
 * @see ry-select
 */

import { RyElement } from '../core/ry-element.js';

// --- Fuzzy match ---

interface FuzzyResult {
  score: number;
  indices: number[];
}

function fuzzyMatch(query: string, text: string): FuzzyResult | null {
  const lq = query.toLowerCase();
  const lt = text.toLowerCase();

  // Prefix match — best score
  if (lt.startsWith(lq)) {
    return { score: 1, indices: Array.from({ length: lq.length }, (_, i) => i) };
  }

  // Substring match — good score
  const subIdx = lt.indexOf(lq);
  if (subIdx >= 0) {
    return { score: 0.8, indices: Array.from({ length: lq.length }, (_, i) => subIdx + i) };
  }

  // Fuzzy match — chars in order with gaps
  const indices: number[] = [];
  let qi = 0;
  for (let ti = 0; ti < lt.length && qi < lq.length; ti++) {
    if (lt[ti] === lq[qi]) {
      indices.push(ti);
      qi++;
    }
  }
  if (qi < lq.length) return null;

  // Score: reward consecutive chars
  let consecutiveBonus = 0;
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === (indices[i - 1] ?? 0) + 1) consecutiveBonus++;
  }
  const score = 0.3 + (consecutiveBonus / Math.max(indices.length - 1, 1)) * 0.4;
  return { score, indices };
}

function highlightText(text: string, indices: number[]): string {
  if (indices.length === 0) return escapeHtml(text);
  const set = new Set(indices);
  let result = '';
  let inMark = false;
  for (let i = 0; i < text.length; i++) {
    const matched = set.has(i);
    if (matched && !inMark) { result += '<mark>'; inMark = true; }
    if (!matched && inMark) { result += '</mark>'; inMark = false; }
    result += escapeHtml(text[i] ?? '');
  }
  if (inMark) result += '</mark>';
  return result;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// --- Placeholder elements ---

class RySearchItem extends HTMLElement {}
if (!customElements.get('ry-search-item')) {
  customElements.define('ry-search-item', RySearchItem);
}

class RySearchGroup extends HTMLElement {}
if (!customElements.get('ry-search-group')) {
  customElements.define('ry-search-group', RySearchGroup);
}

// --- Main component ---

interface ItemEntry {
  value: string;
  label: string;
  el: HTMLElement;
  order: number;
  groupEl: HTMLElement | null;
}

let searchListIdCounter = 0;

export class RySearchList extends RyElement {
  #listId = '';
  #highlightedIndex = -1;
  #highlightSource: 'keyboard' | 'mouse' = 'keyboard';
  #items: ItemEntry[] = [];

  setup(): void {
    this.#listId = `ry-search-list-${++searchListIdCounter}`;
    this.#transformMarkup();

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) {
      this.on(input, 'input', this.#handleInput);
    }
    // Listen on document so arrow keys work even after blur/escape
    this.on(document, 'keydown', this.#handleKeydown);
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'mouseover', this.#handleMouseover);
  }

  #transformMarkup(): void {
    const placeholder = this.getAttribute('placeholder') ?? 'Search...';
    const groups = [...this.querySelectorAll('ry-search-group')];
    const hasGroups = groups.length > 0;

    let itemIndex = 0;
    const items: ItemEntry[] = [];

    let listContent = '';

    if (hasGroups) {
      groups.forEach((group, gi) => {
        const label = group.getAttribute('label') ?? '';
        const groupItems = [...group.querySelectorAll('ry-search-item')];
        let groupContent = `
          <div data-ry-target="group-header" class="ry-search-list__group-header">${escapeHtml(label)}</div>
        `;
        groupItems.forEach(item => {
          const value = item.getAttribute('value') ?? item.textContent?.trim() ?? '';
          const itemLabel = item.textContent?.trim() ?? '';
          groupContent += `
            <div data-ry-target="item" class="ry-search-list__item"
                 role="option" id="${this.#listId}-item-${itemIndex}"
                 data-value="${escapeHtml(value)}" data-ry-order="${itemIndex}"
                 aria-selected="false">
              <span data-ry-target="item-label">${escapeHtml(itemLabel)}</span>
            </div>
          `;
          items.push({ value, label: itemLabel, el: null as unknown as HTMLElement, order: itemIndex, groupEl: null });
          itemIndex++;
        });
        listContent += `<div data-ry-target="group" class="ry-search-list__group" data-group-index="${gi}">${groupContent}</div>`;
      });
    } else {
      const flatItems = [...this.querySelectorAll('ry-search-item')];
      flatItems.forEach(item => {
        const value = item.getAttribute('value') ?? item.textContent?.trim() ?? '';
        const itemLabel = item.textContent?.trim() ?? '';
        listContent += `
          <div data-ry-target="item" class="ry-search-list__item"
               role="option" id="${this.#listId}-item-${itemIndex}"
               data-value="${escapeHtml(value)}" data-ry-order="${itemIndex}"
               aria-selected="false">
            <span data-ry-target="item-label">${escapeHtml(itemLabel)}</span>
          </div>
        `;
        items.push({ value, label: itemLabel, el: null as unknown as HTMLElement, order: itemIndex, groupEl: null });
        itemIndex++;
      });
    }

    this.innerHTML = `
      <div data-ry-target="search" class="ry-search-list__search">
        <input data-ry-target="input" class="ry-search-list__input"
               type="text" placeholder="${escapeHtml(placeholder)}"
               autocomplete="off"
               role="combobox" aria-expanded="true"
               aria-controls="${this.#listId}" aria-autocomplete="list">
      </div>
      <div data-ry-target="list" class="ry-search-list__list"
           role="listbox" id="${this.#listId}">
        ${listContent}
      </div>
      <div data-ry-target="empty" class="ry-search-list__empty" hidden>No results</div>
    `;

    // Wire up element references
    const allItems = this.$$<HTMLElement>('[data-ry-target="item"]');
    allItems.forEach((el, i) => {
      const entry = items[i];
      if (entry) {
        entry.el = el;
        entry.groupEl = el.closest('[data-ry-target="group"]') as HTMLElement | null;
      }
    });

    this.#items = items;
  }

  // --- Filter ---

  #handleInput = (): void => {
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (!input) return;
    const query = input.value.trim();
    this.#filterItems(query);
  };

  #filterItems(query: string): void {
    let visibleCount = 0;

    if (!query) {
      // Restore all items in original order
      this.#items.forEach(item => {
        item.el.removeAttribute('hidden');
        const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
        if (labelSpan) labelSpan.innerHTML = escapeHtml(item.label);
      });
      // Show all groups
      this.$$<HTMLElement>('[data-ry-target="group"]').forEach(g => g.removeAttribute('hidden'));
      visibleCount = this.#items.length;
      // Restore original order
      this.#restoreOrder();
    } else {
      // Score and filter
      const scored: Array<{ item: ItemEntry; result: FuzzyResult }> = [];
      this.#items.forEach(item => {
        const result = fuzzyMatch(query, item.label);
        if (result) {
          item.el.removeAttribute('hidden');
          const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
          if (labelSpan) labelSpan.innerHTML = highlightText(item.label, result.indices);
          scored.push({ item, result });
          visibleCount++;
        } else {
          item.el.setAttribute('hidden', '');
          const labelSpan = item.el.querySelector('[data-ry-target="item-label"]');
          if (labelSpan) labelSpan.innerHTML = escapeHtml(item.label);
        }
      });

      // Sort visible items by score (highest first)
      scored.sort((a, b) => b.result.score - a.result.score);

      // Reorder DOM — sort within groups or flat list
      const groupMap = new Map<HTMLElement | null, Array<{ item: ItemEntry; result: FuzzyResult }>>();
      scored.forEach(s => {
        const key = s.item.groupEl;
        const arr = groupMap.get(key) ?? [];
        arr.push(s);
        groupMap.set(key, arr);
      });

      groupMap.forEach((entries, groupEl) => {
        const container = groupEl ?? this.$('[data-ry-target="list"]');
        if (!container) return;
        entries.forEach(e => container.appendChild(e.item.el));
      });

      // Hide/show groups based on visible children
      this.$$<HTMLElement>('[data-ry-target="group"]').forEach(g => {
        const hasVisible = g.querySelector('[data-ry-target="item"]:not([hidden])');
        if (hasVisible) {
          g.removeAttribute('hidden');
        } else {
          g.setAttribute('hidden', '');
        }
      });
    }

    // Empty state
    const emptyEl = this.$('[data-ry-target="empty"]');
    if (emptyEl) {
      if (visibleCount === 0) {
        emptyEl.removeAttribute('hidden');
      } else {
        emptyEl.setAttribute('hidden', '');
      }
    }

    // Reset highlight
    this.#highlightedIndex = -1;
    this.$$('[data-ry-target="item"][data-highlighted]').forEach(el => {
      el.removeAttribute('data-highlighted');
    });

    this.emit('filter', { query, count: visibleCount });
  }

  #restoreOrder(): void {
    const list = this.$('[data-ry-target="list"]');
    if (!list) return;

    // Check if groups exist
    const groups = this.$$<HTMLElement>('[data-ry-target="group"]');
    if (groups.length > 0) {
      groups.forEach(g => {
        const items = [...g.querySelectorAll<HTMLElement>('[data-ry-target="item"]')];
        items.sort((a, b) => Number(a.dataset.ryOrder ?? 0) - Number(b.dataset.ryOrder ?? 0));
        items.forEach(el => g.appendChild(el));
      });
    } else {
      const items = [...list.querySelectorAll<HTMLElement>('[data-ry-target="item"]')];
      items.sort((a, b) => Number(a.dataset.ryOrder ?? 0) - Number(b.dataset.ryOrder ?? 0));
      items.forEach(el => list.appendChild(el));
    }
  }

  // --- Keyboard ---

  #handleKeydown = (event: KeyboardEvent): void => {
    // Don't capture if user is typing in another input/textarea
    const active = document.activeElement;
    const myInput = this.$('[data-ry-target="input"]');
    if (active && active !== myInput && active !== document.body &&
        (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement ||
         (active as HTMLElement).isContentEditable)) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightNext();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightPrev();
        break;

      case 'Home':
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        this.#highlightIndex(0);
        break;

      case 'End': {
        event.preventDefault();
        this.#highlightSource = 'keyboard';
        const visible = this.#visibleItems();
        this.#highlightIndex(visible.length - 1);
        break;
      }

      case 'Enter':
        if (this.#highlightedIndex >= 0) {
          event.preventDefault();
          const visible = this.#visibleItems();
          const item = visible[this.#highlightedIndex];
          if (item) this.#selectItem(item);
        }
        break;

      case 'Escape': {
        event.preventDefault();
        const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
        if (input) {
          input.value = '';
          input.blur();
        }
        this.#filterItems('');
        break;
      }

      default:
        // Printable char — focus the search input so typing filters
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
          if (input && active !== input) {
            input.focus();
            // Let the keypress flow through to the input naturally
          }
        }
    }
  };

  // --- Click / Hover ---

  #handleClick = (event: MouseEvent): void => {
    const target = event.target as Element;
    const itemEl = target.closest('[data-ry-target="item"]');
    if (itemEl instanceof HTMLElement && !itemEl.hasAttribute('hidden')) {
      this.#selectItem(itemEl);
    }
  };

  #handleMouseover = (event: MouseEvent): void => {
    const target = event.target as Element;
    const itemEl = target.closest('[data-ry-target="item"]');
    if (!(itemEl instanceof HTMLElement) || itemEl.hasAttribute('hidden')) return;
    const visible = this.#visibleItems();
    const index = visible.indexOf(itemEl);
    if (index >= 0) {
      this.#highlightSource = 'mouse';
      this.#highlightIndex(index);
    }
  };

  // --- Highlight ---

  #visibleItems(): HTMLElement[] {
    return this.$$<HTMLElement>('[data-ry-target="item"]:not([hidden])');
  }

  #highlightIndex(index: number): void {
    const visible = this.#visibleItems();
    if (index < 0 || index >= visible.length) return;

    this.$$('[data-ry-target="item"][data-highlighted]').forEach(el => {
      el.removeAttribute('data-highlighted');
    });

    const item = visible[index];
    if (!item) return;

    item.setAttribute('data-highlighted', '');
    if (this.#highlightSource === 'keyboard') {
      item.scrollIntoView({ block: 'center' });
    }
    this.#highlightedIndex = index;

    const input = this.$('[data-ry-target="input"]');
    if (input) {
      input.setAttribute('aria-activedescendant', item.id);
    }

    if (this.#highlightSource === 'keyboard') {
      const value = item.dataset.value ?? '';
      const labelSpan = item.querySelector('[data-ry-target="item-label"]');
      const label = labelSpan?.textContent?.trim() ?? '';
      this.emit('highlight', { value, label });
    }
  }

  #highlightNext(): void {
    const visible = this.#visibleItems();
    if (this.#highlightedIndex < 0) {
      this.#highlightIndex(this.#selectedVisibleIndex(visible));
      return;
    }
    const next = Math.min(this.#highlightedIndex + 1, visible.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    if (this.#highlightedIndex < 0) {
      this.#highlightIndex(this.#selectedVisibleIndex(this.#visibleItems()));
      return;
    }
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  #selectedVisibleIndex(visible: HTMLElement[]): number {
    const selectedEl = this.$('[data-ry-target="item"][aria-selected="true"]');
    if (selectedEl instanceof HTMLElement) {
      const idx = visible.indexOf(selectedEl);
      if (idx >= 0) return idx;
    }
    return 0;
  }

  // --- Selection ---

  #selectItem(itemEl: HTMLElement): void {
    const value = itemEl.dataset.value ?? '';
    const labelSpan = itemEl.querySelector('[data-ry-target="item-label"]');
    const label = labelSpan?.textContent?.trim() ?? '';

    if (!this.emit('beforeselect', { value, label, previous: this.value })) return;

    // Update selected state
    this.$$('[data-ry-target="item"]').forEach(el => {
      el.setAttribute('aria-selected', 'false');
    });
    itemEl.setAttribute('aria-selected', 'true');
    this.setAttribute('value', value);

    // Clear search and restore full list with selection visible
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input && input.value) {
      input.value = '';
      this.#filterItems('');
    }

    // Scroll selected item into view in the sidebar (keyboard only)
    if (this.#highlightSource === 'keyboard') {
      itemEl.scrollIntoView({ block: 'center' });
    }

    this.emit('select', { value, label });
  }

  // --- Public API ---

  get value(): string {
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    const item = this.$<HTMLElement>(`[data-ry-target="item"][data-value="${val}"]`);
    if (item) this.#selectItem(item);
  }

  clear(): void {
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) input.value = '';
    this.#filterItems('');
  }
}

customElements.define('ry-search-list', RySearchList);
